import React, { useState } from "react";
import TextField from "@mui/material/TextField";
import KeyIcon from "@mui/icons-material/Key";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import axios from "axios";
import { API_PWD_RESET_CONFIRM } from "../constants";
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Button from "@mui/material/Button";
import { extractServerErrorMessage } from "../utilities/errorParsing";

function ForgotPasswordChange({
  newPassword,
  setNewPassword,
  confirmPassword,
  setConfirmPassword,
  setPasswordResetFlow,
  uid,
  token,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [linkExpired, setLinkExpired] = useState(false);

  const handleNewPasswordChange = (event) => {
    setNewPassword(event.target.value);
    if (errorMessage) setErrorMessage("");
  };

  const handleConfirmPasswordChange = (event) => {
    setConfirmPassword(event.target.value);
    if (errorMessage) setErrorMessage("");
  };

  const handleResetPassword = async () => {
    if (submitting) return;

    if (newPassword !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }
    if (newPassword.length < 8) {
      setErrorMessage("Password must be at least 8 characters.");
      return;
    }

    setSubmitting(true);
    setErrorMessage("");

    try {
      await axios.post(API_PWD_RESET_CONFIRM, {
        new_password1: newPassword,
        new_password2: confirmPassword,
        uid,
        token,
      });
      setPasswordResetFlow(6);
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;

      if (status === 429) {
        setErrorMessage("Too many attempts. Please wait and try again.");
      } else if (status === 400 && (data?.token || data?.uid)) {
        // Django's password reset confirm returns {token: [...]} / {uid: [...]}
        // when the link is expired, already used, or malformed.
        setLinkExpired(true);
      } else if (status === 400) {
        setErrorMessage(
          extractServerErrorMessage(data) ||
            "That password isn't accepted. Try one with at least 8 characters that isn't too common or too close to your email."
        );
      } else {
        setErrorMessage(
          "We couldn't reset your password right now. Please try again in a few minutes."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const [showPassword, setShowPassword] = React.useState(false);
  const handleClickShowPassword = () => setShowPassword(!showPassword);

  const [showPassword2, setShowPassword2] = React.useState(false);
  const handleClickShowPassword2 = () => setShowPassword2(!showPassword2);

  if (linkExpired) {
    return (
      <div className="w-full h-screen flex flex-col items-center justify-center">
        <div className="w-96 flex flex-col items-center justify-center">
          <div className="h-24 w-24 bg-violet-50 flex items-center justify-center rounded-full">
            <div className="h-16 w-16 bg-violet-100 flex items-center justify-center rounded-full text-violet-700">
              <KeyIcon style={{ fontSize: 40 }} />
            </div>
          </div>
          <div className="mt-5 flex flex-col justify-center items-center">
            <h3 className="text-4xl text-slate-900 text-center">Link expired</h3>
            <p className="text-slate-500 mt-4 text-base text-center">
              This password reset link has expired or has already been used. Request a new one to continue.
            </p>
          </div>
          <div className="w-full mt-8">
            <Button
              onClick={() => setPasswordResetFlow(1)}
              fullWidth
              variant="contained"
              sx={{
                textTransform: "none",
                borderRadius: "50px",
                paddingY: "14px",
                backgroundColor: "#6d28d9",
                "&:hover": { backgroundColor: "#5b21b6" },
                fontSize: "16px",
              }}
            >
              <span style={{ color: "white" }}>Request a new link</span>
            </Button>
          </div>
          <button
            className="mt-4 py-4 px-6 text-slate-500 hover:text-slate-900"
            onClick={() => setPasswordResetFlow(0)}
          >
            <KeyboardBackspaceIcon /> Back to log in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-screen flex flex-col items-center justify-center">
      <div className="w-96 flex flex-col items-center justify-center">
        <div className="h-24 w-24 bg-violet-50 flex items-center justify-center rounded-full">
          <div className="h-16 w-16 bg-violet-100 flex items-center justify-center rounded-full text-violet-700">
            <KeyIcon style={{ fontSize: 40 }} />
          </div>
        </div>
        <div className="mt-5 flex flex-col justify-center items-center">
          <h3 className="text-4xl text-slate-900">Set New Password</h3>
          <p className="text-slate-500 mt-4 text-lg text-center">
            Your password must be different than previously used passwords
          </p>
        </div>
        <div className="w-full mt-8">
          <div>
            <p className="text-slate-500 text-sm">New password</p>
            <div className="mt-1.5">
              <TextField
                variant="outlined"
                required
                fullWidth
                name="new-password"
                placeholder="Enter new password"
                type={showPassword ? "text" : "password"}
                id="new-password"
                value={newPassword}
                onChange={handleNewPasswordChange}
                autoComplete="new-password"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px !important",
                    height: "55px",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6d28d9",
                    },
                    backgroundColor: "white"
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                      >
                        {showPassword ? <VisibilityIcon style={{ fontSize: "22px" }} /> : <VisibilityOffIcon style={{ fontSize: "22px" }} />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </div>
          </div>
          <p className="text-slate-500 mt-1 text-sm">
            Must be a minimum of 8 letters
          </p>
        </div>
        <div className="w-full mt-8">
          <div>
            <p className="text-slate-500 text-sm">Confirm new password</p>
            <div className="mt-1.5">
              <TextField
                variant="outlined"
                required
                fullWidth
                name="confirm-password"
                placeholder="Confirm new password"
                type={showPassword2 ? "text" : "password"}
                id="confirm-password"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                autoComplete="new-password"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px !important",
                    height: "55px",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6d28d9",
                    },
                    backgroundColor: "white"
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword2}
                      >
                        {showPassword2 ? <VisibilityIcon style={{ fontSize: "22px" }} /> : <VisibilityOffIcon style={{ fontSize: "22px" }} />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </div>
          </div>
          {errorMessage && (
            <p className="text-rose-600 text-sm mt-2">{errorMessage}</p>
          )}
        </div>
        <div className="w-full mt-9">
          <Button
            onClick={handleResetPassword}
            disabled={submitting}
            fullWidth
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "50px",
              paddingY: "14px",
              backgroundColor: "#6d28d9",
              "&:hover": {
                backgroundColor: "#5b21b6",
              },
              "&.Mui-disabled": {
                backgroundColor: "#c4b5fd",
                color: "white",
              },
              fontSize: "16px",
            }}
          >
            <span style={{ color: "white" }}>
              {submitting ? "Resetting…" : "Reset Password"}
            </span>
          </Button>
        </div>
        <button
          className="mt-4 py-4 px-6 text-slate-500 hover:text-slate-900"
          onClick={() => setPasswordResetFlow(0)}
        >
          <KeyboardBackspaceIcon /> Back to log in
        </button>
      </div>
    </div>
  );
}

export default ForgotPasswordChange;
